import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import api from '../../../api';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openJobDialog, setOpenJobDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    roles: [],
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadUsers();
    loadRoles();
    loadJobs();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await api.get('/roles');
      setRoles(response.data.roles || response.data.data || []);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const loadJobs = async () => {
    try {
      const response = await api.get('/public-applications/jobs');
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        roles: roles.filter(r => user.roles.includes(r.name)).map(r => r.id),
      });
    } else {
      setSelectedUser(null);
      setFormData({ name: '', email: '', password: '', roles: [] });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setError('');
  };

  const handleOpenJobDialog = async (user) => {
    setSelectedUser(user);
    try {
      const response = await api.get(`/users/${user.id}/allowed-jobs`);
      const allowedJobs = response.data.data || [];
      setSelectedJobs(allowedJobs.map(j => j.job_id));
      setOpenJobDialog(true);
    } catch (error) {
      console.error('Error loading user jobs:', error);
      setError('Failed to load user jobs');
    }
  };

  const handleCloseJobDialog = () => {
    setOpenJobDialog(false);
    setSelectedUser(null);
    setSelectedJobs([]);
  };

  const handleSaveJobs = async () => {
    try {
      await api.post(`/users/${selectedUser.id}/allowed-jobs`, {
        job_ids: selectedJobs,
      });
      setSuccess('Jobs assigned successfully!');
      handleCloseJobDialog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving jobs:', error);
      setError('Failed to assign jobs');
    }
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    try {
      if (selectedUser) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          roles: formData.roles,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.put(`/users/${selectedUser.id}`, updateData);
        setSuccess('User updated successfully!');
      } else {
        await api.post('/users', formData);
        setSuccess('User created successfully!');
      }
      loadUsers();
      handleCloseDialog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving user:', error);
      setError(error.response?.data?.message || 'Failed to save user');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/users/${userId}`);
        setSuccess('User deleted successfully!');
        loadUsers();
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        console.error('Error deleting user:', error);
        setError(error.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const getRoleColor = (roleName) => {
    const colors = {
      admin: 'error',
      super_admin: 'secondary',
      evaluator: 'primary',
      hr: 'success',
    };
    return colors[roleName] || 'default';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Users Management"
        subtitle="Manage system users and their permissions"
        icon={PersonAddIcon}
      />

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#29204C', '&:hover': { bgcolor: '#1a0f2e' } }}
        >
          Add New User
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Roles</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {user.roles?.map((role) => (
                        <Chip
                          key={role}
                          label={role}
                          size="small"
                          color={getRoleColor(role)}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    {user.roles?.includes('evaluator') && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenJobDialog(user)}
                        sx={{ color: '#29204C', mr: 1 }}
                        title="Assign Jobs"
                      >
                        <WorkIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(user)}
                      sx={{ color: '#2196F3', mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(user.id)}
                      sx={{ color: '#f44336' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <TextField
              label={selectedUser ? 'Password (leave blank to keep current)' : 'Password'}
              type="password"
              fullWidth
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={formData.roles}
                onChange={(e) => setFormData({ ...formData, roles: e.target.value })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) =>
                  roles
                    .filter((r) => selected.includes(r.id))
                    .map((r) => r.name)
                    .join(', ')
                }
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ bgcolor: '#29204C', '&:hover': { bgcolor: '#1a0f2e' } }}
          >
            {selectedUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job Assignment Dialog */}
      <Dialog
        open={openJobDialog}
        onClose={handleCloseJobDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Jobs to {selectedUser?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select which jobs this evaluator can review and evaluate
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Allowed Jobs</InputLabel>
              <Select
                multiple
                value={selectedJobs}
                onChange={(e) => setSelectedJobs(e.target.value)}
                input={<OutlinedInput label="Allowed Jobs" />}
                renderValue={(selected) =>
                  jobs
                    .filter((j) => selected.includes(j.job_id))
                    .map((j) => j.job_title || j.posting_title)
                    .join(', ')
                }
              >
                {jobs.map((job) => (
                  <MenuItem key={job.job_id} value={job.job_id}>
                    {job.job_title || job.posting_title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseJobDialog}>Cancel</Button>
          <Button
            onClick={handleSaveJobs}
            variant="contained"
            sx={{ bgcolor: '#29204C', '&:hover': { bgcolor: '#1a0f2e' } }}
          >
            Save Jobs
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
